import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import CompareBar from '@/components/CompareBar';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { setTenantSlug } from './utils/api';

// Platform Pages (no tenant context)
import PlatformLandingPage from './pages/platform/PlatformLandingPage';
import PlatformLoginPage from './pages/platform/PlatformLoginPage';
import PlatformSignupPage from './pages/platform/PlatformSignupPage';
import PlatformAdminDashboard from './pages/platform/PlatformAdminDashboard';

// Tenant Pages
import HomePage from './pages/HomePage';
import ProductListingPage from './pages/ProductListingPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import ComparePage from './pages/ComparePage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import PayPalSuccessPage from './pages/PayPalSuccessPage';
import CustomerDashboard from './pages/CustomerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import FittingWizardPage from './pages/FittingWizardPage';
import ContactPage from './pages/ContactPage';
import ShippingPage from './pages/ShippingPage';
import ReturnsPage from './pages/ReturnsPage';
import FAQPage from './pages/FAQPage';
import TenantNotFound from './pages/TenantNotFound';
import AuthCallback from './components/auth/AuthCallback';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Component to sync tenant slug with API layer
function TenantSync({ children }) {
  const { tenantSlug } = useParams();
  
  useEffect(() => {
    setTenantSlug(tenantSlug);
  }, [tenantSlug]);
  
  return children;
}

// Loading spinner for tenant resolution
function TenantLoading() {
  return (
    <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6741] mx-auto mb-4"></div>
        <p className="text-[#5A6B5C]">Loading store...</p>
      </div>
    </div>
  );
}

// Tenant layout that validates tenant before rendering children
function TenantLayout({ children }) {
  const { isLoading, isValid, tenantSlug, error } = useTenant();
  
  if (isLoading) {
    return <TenantLoading />;
  }
  
  if (!isValid) {
    return <TenantNotFound slug={tenantSlug} canRetry={error !== 'Tenant not found'} />;
  }
  
  return children;
}

// Inner routes (rendered after tenant validation)
function TenantInnerRoutes() {
  const location = useLocation();
  
  // Handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/category/:category" element={<ProductListingPage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/checkout/paypal/success" element={<PayPalSuccessPage />} />
      <Route path="/fitting-wizard" element={<FittingWizardPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/shipping" element={<ShippingPage />} />
      <Route path="/returns" element={<ReturnsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      {/* Catch-all within tenant - show 404 */}
      <Route path="*" element={<TenantNotFound slug={null} />} />
    </Routes>
  );
}

// Tenant routes wrapper
function TenantRoutes() {
  const { tenantSlug } = useParams();
  
  return (
    <TenantProvider tenantSlug={tenantSlug}>
      <TenantSync>
        <TenantLayout>
          <TenantInnerRoutes />
          <CompareBar />
        </TenantLayout>
      </TenantSync>
    </TenantProvider>
  );
}

function AppRouter() {
  return (
    <Routes>
      {/* Platform routes (no tenant context) */}
      <Route path="/" element={<PlatformLandingPage />} />
      <Route path="/login" element={<PlatformLoginPage />} />
      <Route path="/signup" element={<PlatformSignupPage />} />
      <Route path="/platform/admin/*" element={<PlatformAdminDashboard />} />
      
      {/* Tenant routes - validation happens inside TenantRoutes */}
      <Route path="/:tenantSlug/*" element={<TenantRoutes />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
